/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useMemo, useCallback} from "react";
import classes from "@/app/components/algolia/HitsPerPageSelect/hitsPerPageSelectStyles.module.scss";
import {useHitsPerPage} from "react-instantsearch";
import productsConfig from "@/app/config/products.config";
import {useTranslation} from "next-i18next";
import {MenuItem, Select, SelectChangeEvent} from "@mui/material";
import {useRouter} from "next/router";

const HitsPerPageSelect = (props:{
    className?:string,
}) => {
    const {t} = useTranslation();
    const router = useRouter();

    const hitsPerPageValues = productsConfig.productsPerPage;
    const productListLimit = router.query.product_list_limit;
    const pageSize = Number(productListLimit);
    const validPageSize = !isNaN(pageSize) && hitsPerPageValues.includes(pageSize) ? pageSize : productsConfig.productsPerPageDefault;

    const itemsForAlgolia = useMemo(
        () =>
            productsConfig.productsPerPage.map((pagesQty) => ({
                label: pagesQty.toString(),
                value: pagesQty,
                default: pagesQty === validPageSize,
            })),
        [validPageSize]
    );

    const { items, refine } = useHitsPerPage({ items: itemsForAlgolia });

    const { value: currentValue } = items.find(({ isRefined }) => isRefined) || {};

    const changePageSizeHandler = useCallback(
        (event: SelectChangeEvent) => {
            refine(Number(event.target.value));
            router.push(
                {
                    pathname: router.pathname,
                    query: {
                        ...router.query,
                        product_list_limit: event.target.value,
                    }
                },
                undefined,
                {shallow: true}
            );
        },
        [refine, router]
    );

    return <div className={`${classes['show-wrapper']} ${props.className ?? ''}`}>
        <span className={classes['show-first-label']}>{t('Show')}</span>
            <Select
                name="show-per-page"
                id="show-per-page"
                className={`${classes['show-select']}`}
                onChange={changePageSizeHandler}
                value={String(currentValue)}
            >
                {items.map((option) => {
                    return <MenuItem key={option.value} value={option.value}>{t(option.label)}</MenuItem>
                })}
            </Select>
        <span>{t('Per page')}</span>
    </div>
}

export default React.memo(HitsPerPageSelect);